import { useQuery } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { useDebouncedSave } from "../../hooks/useDebouncedSave";
import { useDepartmentById } from "../../hooks/useDepartmentById";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useDepartmentJobs } from "../../hooks/useDepartmentJobs";
import { useDepartmentNurseTypes } from "../../hooks/useDepartmentNurseTypes";
import { useRevealWage } from "../../hooks/useRevealWage";
import { useRestoreUserSettings } from "../../hooks/useUserSettings";
import { backendService } from "../../services/backend.service";
import { hasHrisIntegrationOfType } from "../../utils/hrisIntegrations";
import {
	hppdTargetAverage,
	type ManagementCenterHPPDResponse,
	type ManagementCenterPPDResponse,
	type ManagementCenterStaffMixResponse,
	type NurseFullRole,
	type OtMode,
	StatsMode,
	type UserSettings,
} from "../../utils/models";
import { useManagementCenterView } from "./useManagementCenterView";
import { type RoleJobSelection, useShiftHourScope } from "./useShiftHourScope";
import { getLatestAvailableDate, MC_QUERY_DEFAULTS, parseDateFromSearchParams } from "./utils";

/**
 * Persist a start/end date range in the URL search params so reloads and shared
 * links keep the selected timeframe. Writes with `{ replace: true }` to avoid
 * polluting browser history. Pass `enabled: false` to opt out without breaking
 * the rules of hooks.
 */
export const useSyncDateRangeToSearchParams = (startDate?: DateTime, endDate?: DateTime, enabled = true) => {
	const [, setSearchParams] = useSearchParams();
	useEffect(() => {
		if (!enabled) return;
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				const isoStart = startDate?.toISODate();
				const isoEnd = endDate?.toISODate();
				if (isoStart) next.set("startDate", isoStart);
				else next.delete("startDate");
				if (isoEnd) next.set("endDate", isoEnd);
				else next.delete("endDate");
				return next;
			},
			{ replace: true },
		);
	}, [enabled, setSearchParams, startDate, endDate]);
};

/** Report date range, seeded from `?startDate`/`?endDate` and optionally kept in sync with them. */
export const useManagementCenterDateRange = ({
	defaultDays,
	endDateFallback,
	syncToSearchParams,
}: {
	defaultDays: number;
	/** End date when the URL has none; undefined leaves the range open-ended. */
	endDateFallback: DateTime | undefined;
	syncToSearchParams: boolean;
}) => {
	const { timezone } = useDepartmentFlags();
	const [searchParams] = useSearchParams();
	// Anchor the default start to `getLatestAvailableDate` (the latest settled day), NOT today — the SAME source
	// ManagementCenterHeader uses. Anchoring the fallback to `now` instead was off by one day from the
	// header, so on a fresh load the parent fired a staff-mix/cost query for the wrong start (e.g. 06-14)
	// before the header corrected it to 06-13 — leaving a second, differently-summed cached response and
	// making the KPI disagree with the network. Sharing the helper keeps the two from drifting again.
	const [sinceDate, setSinceDate] = useState<DateTime>(() =>
		parseDateFromSearchParams(searchParams, "startDate", timezone, getLatestAvailableDate(timezone).minus({ days: defaultDays })),
	);
	const [endDate, setEndDate] = useState<DateTime | undefined>(() => parseDateFromSearchParams(searchParams, "endDate", timezone, endDateFallback));
	const onStartDateChange = useCallback((date: DateTime) => setSinceDate(date), []);
	const onEndDateChange = useCallback((date?: DateTime) => setEndDate(date), []);
	useSyncDateRangeToSearchParams(sinceDate, endDate, syncToSearchParams);
	return { sinceDate, endDate, onStartDateChange, onEndDateChange };
};

/** Debounce window before persisting Management Center settings to the backend. */
const MC_SETTINGS_SAVE_DEBOUNCE_MS = 5_000;
/**
 * Backend (snake_case) columns reset by the "Restore" action — only the MC settings.
 * `satisfies` ties these to the `UserSettings` shape, so renaming/removing a column fails the build.
 */
const MC_RESTORE_FIELDS = [
	"mc_labor_cost",
	"mc_include_holidays",
	"mc_include_late_scheduled",
	"mc_ot_mode",
	"mc_estimate_missing_wage",
] satisfies (keyof UserSettings)[];

export const useManagementCenterFilters = ({
	trackPrefix,
	departmentIdOverride,
	defaultDays = 30,
}: {
	trackPrefix: string;
	departmentIdOverride?: string;
	defaultDays?: number;
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isAnyDemo } = useDepartmentFlags();
	// DEFAULT: pages without a department override use the selected department.
	const departmentId = departmentIdOverride ?? chosenDepartment.department_id;
	const departmentNurseTypes = useDepartmentNurseTypes(departmentId);
	const { track } = useAnalytics();
	const [searchParams, setSearchParams] = useSearchParams();
	// Wage permission gate. Labor cost is wage-sensitive, so we mask its effective value (and block
	// toggling/persisting it) when the user can't view wage — protecting the data layer regardless of UI.
	const canViewWage = useRevealWage();

	const { sinceDate, endDate, onStartDateChange, onEndDateChange } = useManagementCenterDateRange({
		defaultDays,
		endDateFallback: undefined,
		syncToSearchParams: false,
	});

	// Undefined until the user picks: "all roles" grows when a drilled department's roster arrives after mount.
	const [pickedRoles, setPickedRoles] = useState<NurseFullRole[] | undefined>(undefined);
	// DEFAULT: every role until the user narrows the selection — reports open unfiltered.
	const selectedRoles = pickedRoles ?? departmentNurseTypes;
	const onRolesChange = useCallback((roles: NurseFullRole[]) => setPickedRoles(roles), []);
	const rolesForApi = useMemo<NurseFullRole[] | undefined>(() => {
		if (isAnyDemo || selectedRoles.length === departmentNurseTypes.length || selectedRoles.length === 0) {
			return undefined;
		}
		return selectedRoles;
	}, [selectedRoles, departmentNurseTypes.length, isAnyDemo]);

	// INH-3407: Job filter (parallel to roles). State-only, not URL-persisted — matches the
	// `selectedRoles` pattern. The header decides which selector to render based on the
	// `job_management_enabled` feature flag.
	const departmentJobs = useDepartmentJobs(departmentId);
	const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
	const onJobsChange = useCallback(
		(ids: string[]) => {
			setSelectedJobs(ids);
			track(`${trackPrefix}_jobs_changed`, { jobs: ids.join(",") });
		},
		[track, trackPrefix],
	);
	const jobsForApi = useMemo<string[] | undefined>(() => {
		if (isAnyDemo || selectedJobs.length === 0 || selectedJobs.length === departmentJobs.length) {
			return undefined;
		}
		return selectedJobs;
	}, [selectedJobs, departmentJobs.length, isAnyDemo]);
	const roleJobSelection = useMemo<RoleJobSelection>(() => ({ roles: selectedRoles, jobs: selectedJobs }), [selectedRoles, selectedJobs]);
	const restoreRoleJobSelection = useCallback(({ roles, jobs }: RoleJobSelection) => {
		setPickedRoles(roles);
		setSelectedJobs(jobs);
	}, []);
	const shiftHourScope = useShiftHourScope({
		departmentId,
		trackPrefix,
		roleFilter: rolesForApi,
		jobFilter: jobsForApi,
		selection: roleJobSelection,
		restoreSelection: restoreRoleJobSelection,
	});

	// Single/Multi Management Center view, URL-persisted (`?view=multi`).
	const { mcView, onMcViewChange } = useManagementCenterView({ trackPrefix });

	// INH-3407: Shift Pickups per-chart Role/Job toggle (URL-persisted, defaults to "job" so
	// flag-on departments see the new grouping; flag-off pages ignore it).
	const [pickupsGroupBy, setPickupsGroupByRaw] = useState<"role" | "job">(() => (searchParams.get("pickupsGroupBy") === "role" ? "role" : "job"));
	const onPickupsGroupByChange = useCallback(
		(mode: "role" | "job") => {
			setPickupsGroupByRaw(mode);
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (mode === "role") next.set("pickupsGroupBy", "role");
					else next.delete("pickupsGroupBy");
					return next;
				},
				{ replace: true },
			);
			track(`${trackPrefix}_pickups_group_by_changed`, { group_by: mode });
		},
		[setSearchParams, track, trackPrefix],
	);

	// ---- Management Center calculation settings (centralized settings menu) ----
	const { settings, updateSettings, settingsResolved } = useUserSettingsContext();
	const { mutateAsync: restoreUserSettings } = useRestoreUserSettings();
	// Debounced persistence to the backend (5s after the last change).
	const mcSnapshotRef = useRef<Partial<typeof settings>>({});
	// The MC settings the user has actually chosen this session, and the source of truth for two
	// things: what may be persisted, and which fields the rehydrate effect below must leave alone.
	//
	// Until the settings query resolves, the untouched entries of `mcSnapshotRef` are `defaultSettings`
	// guesses, which for the four settings APP-731 did not backfill still disagree with what users have
	// stored. Persisting the whole snapshot then would overwrite a saved value with a guess.
	//
	// Per field rather than the single "has interacted" boolean it replaces: that flag stopped
	// rehydration for *all five* settings, so editing one control before the settings landed stranded
	// the other four on those same guesses for the rest of the session.
	//
	// Accumulated rather than replaced, because the debounce is keyed and the last payload wins — a
	// second pre-resolution toggle would otherwise drop the first. <!-- APP-731 -->
	const mcUserChangesRef = useRef<Partial<typeof settings>>({});
	const debouncedMcSave = useDebouncedSave<Partial<typeof settings>>((payload) => {
		updateSettings(payload).catch(() => {});
	}, MC_SETTINGS_SAVE_DEBOUNCE_MS);
	const scheduleMcSave = useCallback(
		(changed: Partial<typeof settings>) => {
			mcUserChangesRef.current = { ...mcUserChangesRef.current, ...changed };
			// Before the settings resolve, send only what the user chose; after, keep sending the full
			// current object so a single-field change still persists a complete, consistent MC set.
			const base = settingsResolved ? mcSnapshotRef.current : {};
			debouncedMcSave("mc", { ...base, ...mcUserChangesRef.current });
		},
		[debouncedMcSave, settingsResolved],
	);

	const updateMcParams = useCallback(
		// Apply a batch of URL param changes in one history-replace (null clears a param).
		(changes: Record<string, string | null>) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					for (const [key, value] of Object.entries(changes)) {
						if (value === null) next.delete(key);
						else next.set(key, value);
					}
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	// Labor cost toggle
	const [laborCostEnabled, setLaborCostEnabledRaw] = useState(() => {
		const param = searchParams.get("laborCost");
		if (param === "true") return true;
		if (param === "false") return false;
		return settings.mcLaborCost;
	});
	const onLaborCostToggle = useCallback(
		(enabled: boolean) => {
			// Wage-gated: never flip on / persist labor cost without permission, even if the UI calls in.
			if (!canViewWage) return;
			setLaborCostEnabledRaw(enabled);
			updateMcParams({ laborCost: enabled ? "true" : null });
			scheduleMcSave({ mcLaborCost: enabled });
			track(`${trackPrefix}_labor_cost_toggled`, { labor_cost: enabled });
		},
		[canViewWage, track, trackPrefix, updateMcParams, scheduleMcSave],
	);

	// Include holidays toggle (default: false = holidays excluded)
	const [includeHolidays, setIncludeHolidaysRaw] = useState(() => {
		const param = searchParams.get("includeHolidays");
		if (param === "true") return true;
		if (param === "false") return false;
		return settings.mcIncludeHolidays;
	});
	const onIncludeHolidaysToggle = useCallback(
		(enabled: boolean) => {
			setIncludeHolidaysRaw(enabled);
			updateMcParams({ includeHolidays: enabled ? "true" : null });
			scheduleMcSave({ mcIncludeHolidays: enabled });
			track(`${trackPrefix}_include_holidays_toggled`, { include_holidays: enabled });
		},
		[track, trackPrefix, updateMcParams, scheduleMcSave],
	);
	const excludeHolidaysForApi = includeHolidays ? undefined : true;

	// Exclude meal break (FE-only, temporary): always on → breaks always excluded (gross hours
	// without meal break). The menu renders this checked + disabled, so it never toggles.
	const includeBreaks = false;
	const includeBreaksForApi = false;

	const [showAgencyPickups, setShowAgencyPickupsRaw] = useState(() => searchParams.get("showAgencyPickups") === "true");
	const onShowAgencyPickupsToggle = useCallback(
		(enabled: boolean) => {
			setShowAgencyPickupsRaw(enabled);
			updateMcParams({ showAgencyPickups: enabled ? "true" : null });
			track(`${trackPrefix}_show_agency_pickups_toggled`, { show_agency_pickups: enabled });
		},
		[track, trackPrefix, updateMcParams],
	);
	// Only send the param when overriding the server's default (which excludes agency)
	const excludeAgencyForApi = showAgencyPickups ? false : undefined;

	// Include late scheduled toggle (default: false = late-scheduled excluded)
	const [includeLateScheduled, setIncludeLateScheduledRaw] = useState(() => {
		const param = searchParams.get("includeLateScheduled");
		if (param === "true") return true;
		if (param === "false") return false;
		return settings.mcIncludeLateScheduled;
	});
	const onIncludeLateScheduledToggle = useCallback(
		(enabled: boolean) => {
			setIncludeLateScheduledRaw(enabled);
			updateMcParams({ includeLateScheduled: enabled ? "true" : null });
			scheduleMcSave({ mcIncludeLateScheduled: enabled });
			track(`${trackPrefix}_include_late_scheduled_toggled`, { include_late_scheduled: enabled });
		},
		[track, trackPrefix, updateMcParams, scheduleMcSave],
	);
	const includeLateScheduledForApi = includeLateScheduled ? true : undefined;

	// Estimate-missing-wage toggle (default: true — APP-731, which also backfilled existing rows to
	// true, so this is what everyone starts from). When on, cost uses the role/job-average fallback
	// for wageless nurses instead of counting them as $0. The backend param is required, so it is
	// always sent (true or false), unlike the optional toggles above.
	const [estimateMissingWage, setEstimateMissingWageRaw] = useState(() => {
		const param = searchParams.get("estimateMissingWage");
		if (param === "true") return true;
		if (param === "false") return false;
		return settings.mcEstimateMissingWage;
	});
	const onEstimateMissingWageToggle = useCallback(
		(enabled: boolean) => {
			setEstimateMissingWageRaw(enabled);
			updateMcParams({ estimateMissingWage: enabled ? "true" : null });
			scheduleMcSave({ mcEstimateMissingWage: enabled });
			track(`${trackPrefix}_estimate_missing_wage_toggled`, { estimate_missing_wage: enabled });
		},
		[track, trackPrefix, updateMcParams, scheduleMcSave],
	);

	// APP-731: acknowledging the one-time rollout notice. Tracked here rather than in the settings menu
	// so the event carries this surface's `trackPrefix`, like every other setting interaction.
	const onEstimateMissingWageNoticeDismissed = useCallback(() => {
		track(`${trackPrefix}_estimate_missing_wage_notice_dismissed`, {});
	}, [track, trackPrefix]);

	// Wanted mode toggle (persisted in user settings + URL override)
	const isRatioMode = chosenDepartment.statsMode === StatsMode.Ratio;
	const [wantedMode, setWantedModeRaw] = useState<StatsMode | undefined>(() => {
		const urlParam = searchParams.get("wantedMode");
		if (urlParam === StatsMode.PPD_HPPD || urlParam === StatsMode.Ratio) return urlParam;
		if (settings.wantedMode) return settings.wantedMode;
		return isRatioMode ? StatsMode.Ratio : undefined;
	});
	const onWantedModeToggle = useCallback(
		(enabled: boolean) => {
			const mode = enabled ? StatsMode.PPD_HPPD : StatsMode.Ratio;
			setWantedModeRaw(mode);
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.set("wantedMode", mode);
					return next;
				},
				{ replace: true },
			);
			updateSettings({ wantedMode: mode });
			track(`${trackPrefix}_wanted_mode_toggled`, { wanted_mode: mode });
		},
		[track, trackPrefix, setSearchParams, updateSettings],
	);
	const wantedModeForApi = wantedMode;

	const hasPayrollIntegration = hasHrisIntegrationOfType(chosenDepartment, "shifts");
	const [isActual, setIsActual] = useState<boolean>(() => {
		const param = searchParams.get("isActual");
		if (param === "true") return true;
		if (param === "false") return false;
		return hasPayrollIntegration;
	});
	const onActualToggleChange = useCallback(
		(val: boolean) => {
			setIsActual(val);
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (val === hasPayrollIntegration) next.delete("isActual");
					else next.set("isActual", String(val));
					return next;
				},
				{ replace: true },
			);
			track(`${trackPrefix}_actual_toggle_changed`, { is_actual: val });
		},
		[track, trackPrefix, setSearchParams, hasPayrollIntegration],
	);

	// Weekly-OT attribution mode (Trigger vs Actual). Resolution order: URL param → BE user
	// setting → department default ("actual"). URL wins so deep links override.
	const [otMode, setOtModeRaw] = useState<OtMode>(() => {
		const param = searchParams.get("otMode");
		if (param === "actual" || param === "trigger") return param;
		return settings.mcOtMode;
	});

	// Keep the snapshot used by `scheduleMcSave` in sync with the latest committed values, so a
	// single-field change still debounces a complete, current MC object to the backend.
	mcSnapshotRef.current = {
		mcLaborCost: laborCostEnabled,
		mcIncludeHolidays: includeHolidays,
		mcIncludeLateScheduled: includeLateScheduled,
		mcOtMode: otMode,
		mcEstimateMissingWage: estimateMissingWage,
	};

	const onOtModeChange = useCallback(
		(mode: OtMode) => {
			setOtModeRaw(mode);
			// "actual" is the default — only pin the URL when overriding to "trigger".
			updateMcParams({ otMode: mode === "trigger" ? "trigger" : null });
			scheduleMcSave({ mcOtMode: mode });
			track(`${trackPrefix}_ot_mode_changed`, { ot_mode: mode });
		},
		[track, trackPrefix, updateMcParams, scheduleMcSave],
	);

	const onAlignToDayView = useCallback(() => {
		// Align to day view: labor cost off, holidays + late-scheduled on, OT = actual, and estimate
		// missing wage ON — the daily view always includes the role/job-average estimate, so matching it
		// requires the estimate to be on (it now defaults on, but a user who turned it off still needs it
		// forced back on here; the other four settings are what this button mostly exists to align).
		setLaborCostEnabledRaw(false);
		setIncludeHolidaysRaw(true);
		setIncludeLateScheduledRaw(true);
		setOtModeRaw("actual");
		setEstimateMissingWageRaw(true);
		updateMcParams({ laborCost: null, includeHolidays: "true", includeLateScheduled: "true", otMode: null, estimateMissingWage: "true" });
		scheduleMcSave({ mcLaborCost: false, mcIncludeHolidays: true, mcIncludeLateScheduled: true, mcOtMode: "actual", mcEstimateMissingWage: true });
		track(`${trackPrefix}_align_to_day_view`, {});
	}, [track, trackPrefix, updateMcParams, scheduleMcSave]);

	// Save the current MC settings as the department default (overrides all users).
	const onSaveMcDefaults = useCallback(async () => {
		// This overrides every user's row in the department, so it must never run on the
		// pre-resolution `defaultSettings` guesses. The window is the settings round trip. <!-- APP-731 -->
		if (!settingsResolved) return;
		await updateSettings({ ...mcSnapshotRef.current }, true).catch(() => {});
		track(`${trackPrefix}_save_mc_defaults`, {});
	}, [updateSettings, track, trackPrefix, settingsResolved]);

	const onRestoreMcSettings = useCallback(async () => {
		// Restore MC settings to the department default (leaves other user settings untouched).
		const restored = await restoreUserSettings(MC_RESTORE_FIELDS).catch(() => undefined);
		// On a failed restore, leave local state and the rehydrate path untouched so the UI can
		// resync from server settings — recording these as user-chosen would strand stale values.
		if (!restored) return;
		setLaborCostEnabledRaw(restored.mc_labor_cost);
		setIncludeHolidaysRaw(restored.mc_include_holidays);
		setIncludeLateScheduledRaw(restored.mc_include_late_scheduled);
		setOtModeRaw(restored.mc_ot_mode as OtMode);
		setEstimateMissingWageRaw(restored.mc_estimate_missing_wage);
		// Supersede any pending pre-restore debounced save (same key, last write wins) so it
		// can't fire afterward and clobber the restored values.
		scheduleMcSave({
			mcLaborCost: restored.mc_labor_cost,
			mcIncludeHolidays: restored.mc_include_holidays,
			mcIncludeLateScheduled: restored.mc_include_late_scheduled,
			mcOtMode: restored.mc_ot_mode as OtMode,
			mcEstimateMissingWage: restored.mc_estimate_missing_wage,
		});
		// Drop the URL overrides so the restored values take effect.
		updateMcParams({ laborCost: null, includeHolidays: null, includeLateScheduled: null, otMode: null, estimateMissingWage: null });
		track(`${trackPrefix}_restore_mc_settings`, {});
	}, [restoreUserSettings, scheduleMcSave, updateMcParams, track, trackPrefix]);

	useEffect(() => {
		// Hydrate local MC state from server settings when the query resolves after this hook mounts.
		// Skipped per field for anything pinned by a URL param or already chosen by the user this
		// session -- deliberately per field: a single "has interacted" flag meant editing one control
		// before the settings landed left the other four on `defaultSettings` for the rest of the
		// session, which since APP-731 differ from what most users have stored. <!-- APP-731 -->
		const chosen = mcUserChangesRef.current;
		if (searchParams.get("laborCost") === null && chosen.mcLaborCost === undefined) setLaborCostEnabledRaw(settings.mcLaborCost);
		if (searchParams.get("includeHolidays") === null && chosen.mcIncludeHolidays === undefined) setIncludeHolidaysRaw(settings.mcIncludeHolidays);
		if (searchParams.get("includeLateScheduled") === null && chosen.mcIncludeLateScheduled === undefined)
			setIncludeLateScheduledRaw(settings.mcIncludeLateScheduled);
		if (searchParams.get("otMode") === null && chosen.mcOtMode === undefined) setOtModeRaw(settings.mcOtMode);
		if (searchParams.get("estimateMissingWage") === null && chosen.mcEstimateMissingWage === undefined)
			setEstimateMissingWageRaw(settings.mcEstimateMissingWage);
	}, [settings.mcLaborCost, settings.mcIncludeHolidays, settings.mcIncludeLateScheduled, settings.mcOtMode, settings.mcEstimateMissingWage, searchParams]);

	return {
		// Reports wait for saved calculation settings and a reconciled role/job + Shift Hours scope.
		// Callers use this for query enablement and loading skeletons, so a new role never shows
		// results calculated with the previous role's time window. If no options ever loaded, reports
		// run on the current role/job with no Shift Hours filter instead of waiting forever.
		mcSettingsReady: settingsResolved && shiftHourScope.shiftHoursReady,
		sinceDate,
		endDate,
		onStartDateChange,
		onEndDateChange,
		selectedRoles,
		roleOptions: departmentNurseTypes,
		onRolesChange,
		appliedRoleFilter: shiftHourScope.appliedRoleFilter,
		// INH-3407 job filter + shift-pickups group-by toggle
		selectedJobs,
		onJobsChange,
		appliedJobFilter: shiftHourScope.appliedJobFilter,
		pickupsGroupBy,
		onPickupsGroupByChange,
		mcView,
		onMcViewChange,
		selectedShiftHours: shiftHourScope.selectedShiftHours,
		shiftHourOptions: shiftHourScope.shiftHourOptions,
		shiftHoursUpdating: shiftHourScope.shiftHoursUpdating,
		shiftHoursUnavailable: shiftHourScope.shiftHoursUnavailable,
		shiftHoursReady: shiftHourScope.shiftHoursReady,
		onShiftHoursChange: shiftHourScope.onShiftHoursChange,
		shiftHoursForApi: shiftHourScope.shiftHoursForApi,
		isActual,
		onActualToggleChange,
		// Effective (wage-masked) value consumed by the API hooks and the header; the raw user choice
		// stays in state/persistence and returns intact once wage becomes viewable again.
		laborCostEnabled: canViewWage && laborCostEnabled,
		onLaborCostToggle,
		includeHolidays,
		onIncludeHolidaysToggle,
		excludeHolidaysForApi,
		includeBreaks,
		includeBreaksForApi,
		showAgencyPickups,
		onShowAgencyPickupsToggle,
		excludeAgencyForApi,
		includeLateScheduled,
		onIncludeLateScheduledToggle,
		includeLateScheduledForApi,
		estimateMissingWage,
		onEstimateMissingWageToggle,
		onEstimateMissingWageNoticeDismissed,
		wantedMode,
		onWantedModeToggle,
		wantedModeForApi,
		otMode,
		onOtModeChange,
		onAlignToDayView,
		onSaveMcDefaults,
		onRestoreMcSettings,
		searchParams,
		setSearchParams,
	};
};

export const useGetManagementCenterCallIns = ({
	sinceDate,
	endDate,
	roles,
	jobs,
	shiftHours,
	excludeHolidays,
	departmentId: departmentIdOverride,
}: {
	sinceDate: DateTime;
	endDate?: DateTime;
	roles?: string[];
	jobs?: string[];
	shiftHours?: string[];
	excludeHolidays?: boolean;
	departmentId?: string;
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = departmentIdOverride || chosenDepartment.department_id;
	const { track } = useAnalytics();
	return useQuery({
		queryKey: [
			"management-center-call-ins",
			sinceDate.toFormat("yyyy-MM-dd"),
			endDate?.toFormat("yyyy-MM-dd"),
			departmentId,
			roles,
			jobs,
			shiftHours,
			excludeHolidays,
		],
		queryFn: async () => {
			track("fetch_management_center_call_ins", {
				department_id: departmentId,
				sinceDate: sinceDate.toFormat("yyyy-MM-dd"),
				endDate: endDate?.toFormat("yyyy-MM-dd"),
				shift_hours: shiftHours?.join(","),
			});
			const result = await backendService.getManagementCenterCallIns({
				departmentId,
				startDate: sinceDate,
				endDate,
				grouping: true,
				roles,
				jobs,
				shiftHours,
				excludeHolidays,
			});
			return result.data;
		},
		...MC_QUERY_DEFAULTS,
	});
};

export const useGetManagementCenterShiftPickUps = ({
	sinceDate,
	endDate,
	roles,
	jobs,
	groupBy,
	shiftHours,
	excludeHolidays,
	excludeAgency,
	departmentId: departmentIdOverride,
}: {
	sinceDate: DateTime;
	endDate?: DateTime;
	roles?: string[];
	jobs?: string[];
	groupBy?: "role" | "job";
	shiftHours?: string[];
	excludeHolidays?: boolean;
	excludeAgency?: boolean;
	departmentId?: string;
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = departmentIdOverride || chosenDepartment.department_id;
	const { track } = useAnalytics();
	return useQuery({
		queryKey: [
			"management-center-shift-pick-ups",
			sinceDate.toFormat("yyyy-MM-dd"),
			endDate?.toFormat("yyyy-MM-dd"),
			departmentId,
			roles,
			jobs,
			groupBy,
			shiftHours,
			excludeHolidays,
			excludeAgency,
		],
		queryFn: async () => {
			track("fetch_management_center_shift_pick_ups", {
				department_id: departmentId,
				sinceDate: sinceDate.toFormat("yyyy-MM-dd"),
				endDate: endDate?.toFormat("yyyy-MM-dd"),
				shift_hours: shiftHours?.join(","),
			});
			const result = await backendService.getManagementCenterShiftPickUps({
				departmentId,
				startDate: sinceDate,
				endDate,
				grouping: true,
				roles,
				jobs,
				groupBy,
				shiftHours,
				excludeHolidays,
				excludeAgency,
			});
			return result.data;
		},
		...MC_QUERY_DEFAULTS,
	});
};

export const useGetManagementCenterStaffMix = ({
	sinceDate,
	endDate,
	isActual,
	roles,
	jobs,
	laborCost,
	shiftHours,
	excludeHolidays,
	hideWage,
	includeLateScheduled,
	otMode,
	estimateMissingWage,
	// DEFAULT: matches the `enabled = true` convention of the sibling MC hooks (hppd/hours/ppd),
	// so pre-existing callers keep fetching unchanged.
	enabled = true,
	departmentId: departmentIdOverride,
}: {
	sinceDate: DateTime;
	endDate?: DateTime;
	isActual: boolean;
	roles?: string[];
	jobs?: string[];
	laborCost?: boolean;
	shiftHours?: string[];
	excludeHolidays?: boolean;
	hideWage?: boolean;
	includeLateScheduled?: boolean;
	otMode?: OtMode;
	estimateMissingWage: boolean;
	enabled?: boolean;
	departmentId?: string;
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = departmentIdOverride || chosenDepartment.department_id;
	const { track } = useAnalytics();
	return useQuery({
		queryKey: [
			"management-center-staff-mix",
			sinceDate.toFormat("yyyy-MM-dd"),
			endDate?.toFormat("yyyy-MM-dd"),
			departmentId,
			isActual,
			roles,
			jobs,
			laborCost,
			shiftHours,
			excludeHolidays,
			hideWage,
			includeLateScheduled,
			otMode,
			estimateMissingWage,
		],
		queryFn: async () => {
			track("fetch_management_center_staff_mix", {
				department_id: departmentId,
				sinceDate: sinceDate.toFormat("yyyy-MM-dd"),
				endDate: endDate?.toFormat("yyyy-MM-dd"),
				shift_hours: shiftHours?.join(","),
			});
			const result = await backendService.getManagementCenterStaffMix({
				isActual: isActual,
				departmentId,
				startDate: sinceDate,
				endDate,
				grouping: true,
				roles,
				jobs,
				laborCost,
				shiftHours,
				excludeHolidays,
				hideWage,
				includeLateScheduled,
				otMode,
				estimateMissingWage,
			});
			return result.data;
		},
		...MC_QUERY_DEFAULTS,
		enabled,
	});
};

export const useGetManagementCenterHppdStats = ({
	sinceDate,
	endDate,
	roles,
	jobs,
	grouping = true,
	enabled = true,
	shiftHours,
	excludeHolidays,
	includeLateScheduled,
	wantedMode,
	otMode,
	departmentId: departmentIdOverride,
}: {
	sinceDate: DateTime;
	endDate?: DateTime;
	roles?: string[];
	jobs?: string[];
	grouping?: boolean;
	enabled?: boolean;
	shiftHours?: string[];
	excludeHolidays?: boolean;
	includeLateScheduled?: boolean;
	wantedMode?: StatsMode;
	otMode?: OtMode;
	departmentId?: string;
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = departmentIdOverride || chosenDepartment.department_id;
	const { track } = useAnalytics();
	return useQuery({
		queryKey: [
			"management-center-hppd-stats",
			sinceDate.toFormat("yyyy-MM-dd"),
			endDate?.toFormat("yyyy-MM-dd"),
			departmentId,
			roles,
			jobs,
			grouping,
			shiftHours,
			excludeHolidays,
			includeLateScheduled,
			wantedMode,
			otMode,
		],
		queryFn: async () => {
			track("fetch_management_center_hppd_stats", {
				department_id: departmentId,
				sinceDate: sinceDate.toFormat("yyyy-MM-dd"),
				endDate: endDate?.toFormat("yyyy-MM-dd"),
				shift_hours: shiftHours?.join(","),
			});
			const result = await backendService.getManagementCenterHppdStats({
				departmentId,
				startDate: sinceDate,
				endDate,
				roles,
				jobs,
				grouping,
				shiftHours,
				excludeHolidays,
				includeLateScheduled,
				wantedMode,
				otMode,
			});
			return result.data;
		},
		...MC_QUERY_DEFAULTS,
		enabled,
	});
};

export const useGetManagementCenterHoursStats = ({
	sinceDate,
	endDate,
	roles,
	jobs,
	grouping = true,
	enabled = true,
	shiftHours,
	includeLateScheduled,
	wantedMode,
	otMode,
	departmentId: departmentIdOverride,
}: {
	sinceDate: DateTime;
	endDate?: DateTime;
	roles?: string[];
	jobs?: string[];
	grouping?: boolean;
	enabled?: boolean;
	shiftHours?: string[];
	includeLateScheduled?: boolean;
	wantedMode?: StatsMode;
	otMode?: OtMode;
	departmentId?: string;
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = departmentIdOverride || chosenDepartment.department_id;
	const { track } = useAnalytics();
	return useQuery({
		queryKey: [
			"management-center-hours-stats",
			sinceDate.toFormat("yyyy-MM-dd"),
			endDate?.toFormat("yyyy-MM-dd"),
			departmentId,
			roles,
			jobs,
			grouping,
			shiftHours,
			includeLateScheduled,
			wantedMode,
			otMode,
		],
		queryFn: async () => {
			track("fetch_management_center_hours_stats", {
				department_id: departmentId,
				sinceDate: sinceDate.toFormat("yyyy-MM-dd"),
				endDate: endDate?.toFormat("yyyy-MM-dd"),
				shift_hours: shiftHours?.join(","),
			});
			const result = await backendService.getManagementCenterHoursStats({
				departmentId,
				startDate: sinceDate,
				endDate,
				roles,
				jobs,
				grouping,
				shiftHours,
				includeLateScheduled,
				wantedMode,
				otMode,
			});
			return result.data;
		},
		...MC_QUERY_DEFAULTS,
		enabled,
	});
};

export const useGetManagementCenterPpdStats = ({
	sinceDate,
	endDate,
	roles,
	jobs,
	grouping = true,
	enabled = true,
	laborCost,
	shiftHours,
	excludeHolidays,
	hideWage,
	includeLateScheduled,
	wantedMode,
	otMode,
	estimateMissingWage,
	departmentId: departmentIdOverride,
}: {
	sinceDate: DateTime;
	endDate?: DateTime;
	roles?: string[];
	jobs?: string[];
	grouping?: boolean;
	enabled?: boolean;
	laborCost?: boolean;
	shiftHours?: string[];
	excludeHolidays?: boolean;
	hideWage?: boolean;
	includeLateScheduled?: boolean;
	wantedMode?: StatsMode;
	otMode?: OtMode;
	estimateMissingWage: boolean;
	departmentId?: string;
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = departmentIdOverride || chosenDepartment.department_id;
	const { track } = useAnalytics();
	return useQuery({
		queryKey: [
			"management-center-ppd-stats",
			sinceDate.toFormat("yyyy-MM-dd"),
			endDate?.toFormat("yyyy-MM-dd"),
			departmentId,
			roles,
			jobs,
			grouping,
			laborCost,
			shiftHours,
			excludeHolidays,
			hideWage,
			includeLateScheduled,
			wantedMode,
			otMode,
			estimateMissingWage,
		],
		queryFn: async () => {
			track("fetch_management_center_ppd_stats", {
				department_id: departmentId,
				sinceDate: sinceDate.toFormat("yyyy-MM-dd"),
				endDate: endDate?.toFormat("yyyy-MM-dd"),
				shift_hours: shiftHours?.join(","),
			});
			const result = await backendService.getManagementCenterPpdStats({
				departmentId,
				startDate: sinceDate,
				endDate,
				roles,
				jobs,
				grouping,
				laborCost,
				shiftHours,
				excludeHolidays,
				hideWage,
				includeLateScheduled,
				wantedMode,
				otMode,
				estimateMissingWage,
			});
			return result.data;
		},
		...MC_QUERY_DEFAULTS,
		enabled,
	});
};

export type ManagementCenterAveragesAndTotals = {
	avgActualPPD: number;
	avgScheduledPPD: number;
	agencyHours: number;
	overtimeHours: number;
	fulltimeHours: number; // = totalActualHours - agency - overtime
	avgActualHPPD: number;
	avgScheduledHPPD: number;
	totalCost: number; // Σ (actual PPD * census)
	occupancyRate: number | undefined; // avg census / total beds; undefined when the department is unknown
	totalAverageCensus: number;
	totalBeds: number | undefined;
	totalOvertimeCost: number;
	totalAgencyCost: number;
	totalPtoCost: number;
	targetPPD: number;
	targetHPPD: number;
};

export const useGetAverageAndTotalValuesFromAPIData = ({
	hppdData,
	ppdData,
	staffMixData,
	isActual,
	departmentId: departmentIdOverride,
}: {
	staffMixData?: ManagementCenterStaffMixResponse;
	hppdData?: ManagementCenterHPPDResponse;
	ppdData?: ManagementCenterPPDResponse;
	isActual: boolean;
	departmentId?: string;
}): ManagementCenterAveragesAndTotals | undefined => {
	const { chosenDepartment } = useChosenDepartment();
	// Beds for occupancy come from the department shown — the drilled one in the ops backoffice.
	const department = useDepartmentById(departmentIdOverride || chosenDepartment.department_id);
	if (!hppdData || !staffMixData) return undefined;
	// --- helpers ---
	const safe = (n: number | undefined | null) => (Number.isFinite(n as number) ? (n as number) : 0);
	const keys = (...maps: Array<Record<string, number> | undefined>) => Array.from(new Set(maps.flatMap((m) => (m ? Object.keys(m) : []))));

	// Normalize % maps to 0..1 range (auto-detect if values look like 0..100)
	const normalizePctMap = (m?: Record<string, number>) => {
		if (!m) return {};
		const maxVal = Math.max(0, ...Object.values(m).map((v) => safe(v)));
		const factor = maxVal > 1.000001 ? 0.01 : 1; // if any value > 1, treat as 0..100
		const out: Record<string, number> = {};
		for (const k of Object.keys(m)) out[k] = safe(m[k]) * factor;
		return out;
	};

	const nonWeightedAvg = (vMap: Record<string, number>) => {
		let num = 0,
			count = 0;
		for (const d of Object.keys(vMap)) {
			const v = safe(vMap[d]);
			num += v;
			count += 1;
		}
		return count > 0 ? num / count : 0;
	};

	const sumValues = (vMap: Record<string, number>) => {
		let total = 0;
		for (const d of Object.keys(vMap)) {
			const v = safe(vMap[d]);
			total += v;
		}
		return total;
	};

	// --- unwrap data (fall back to empty maps) ---
	const censusH = hppdData?.census ?? {};
	const hppdAct = hppdData?.hppdActual ?? {};
	const hppdSch = hppdData?.hppdScheduled ?? {};

	const censusP = ppdData?.census ?? {};
	const ppdAct = ppdData?.ppdActual ?? {};
	const ppdSch = ppdData?.ppdScheduled ?? {};
	const censusMix = staffMixData?.census ?? {};
	const overtimePct = normalizePctMap(staffMixData?.overtimePercent);
	const agencyPct = normalizePctMap(staffMixData?.agencyPercent);

	// --- totals (hours) from HPPD × census (use ACTUAL for staff-mix hours) ---
	// We'll compute daily actual hours from HPPD actual (hours per patient-day) × census.
	const daysForHours = keys(isActual ? hppdAct : hppdSch, censusH);
	let totalHours = 0;
	const dailyHours: Record<string, number> = {};
	for (const d of daysForHours) {
		const hrs = safe(hppdAct[d]) * safe(censusH[d]);
		dailyHours[d] = hrs;
		totalHours += hrs;
	}

	// Agency & OT hours (apply staff-mix % to actual daily hours)
	const daysForMix = keys(dailyHours, agencyPct, overtimePct, censusMix);
	let agencyHours = 0,
		overtimeHours = 0;
	for (const d of daysForMix) {
		const baseHrs = safe(dailyHours[d]); // actual hours that day
		agencyHours += baseHrs * safe(agencyPct[d]);
		overtimeHours += baseHrs * safe(overtimePct[d]);
	}
	const fulltimeHours = Math.max(0, totalHours - agencyHours - overtimeHours);

	// --- averages (census-weighted) ---
	// HPPD averages (use hppdData.census for weighting)
	const avgActualHPPD = nonWeightedAvg(hppdAct);
	const avgScheduledHPPD = nonWeightedAvg(hppdSch);

	// PPD averages (use ppdData.census for weighting)
	const avgActualPPD = nonWeightedAvg(ppdAct);
	const avgScheduledPPD = nonWeightedAvg(ppdSch);

	// --- total cost over period (Σ actual PPD × census) ---
	let totalActualCost = 0;
	for (const d of keys(ppdAct, censusP)) {
		totalActualCost += safe(ppdAct[d]) * safe(censusP[d]);
	}

	let totalScheduledCost = 0;
	for (const d of keys(ppdSch, censusP)) {
		totalScheduledCost += safe(ppdSch[d]) * safe(censusP[d]);
	}

	const avgCensus = nonWeightedAvg(censusH);
	// Undefined when the department shown isn't in the user's list, so the card says "-" rather than 0% of 0 beds.
	// DEFAULT: a department with no bed count configured keeps reading 0 beds, as before.
	const totalBeds = department ? (department.total_beds ?? 0) : undefined;
	const occupancyRate = totalBeds === undefined ? undefined : (totalBeds > 0 ? avgCensus / totalBeds : 0) * 100;

	const totalOvertimeCost = staffMixData.overtimeCost ? sumValues(staffMixData.overtimeCost) : 0;
	const totalAgencyCost = staffMixData.agencyCost ? sumValues(staffMixData.agencyCost) : 0;
	const totalPtoCost = staffMixData.ptoCost ? sumValues(staffMixData.ptoCost) : 0;

	return {
		avgActualPPD,
		avgScheduledPPD,
		agencyHours,
		overtimeHours,
		fulltimeHours,
		avgActualHPPD,
		avgScheduledHPPD,
		totalCost: isActual ? totalActualCost : totalScheduledCost,
		occupancyRate,
		totalAverageCensus: avgCensus,
		totalBeds,
		totalOvertimeCost,
		totalAgencyCost,
		totalPtoCost,
		targetHPPD: hppdTargetAverage(chosenDepartment.hppd_target),
		targetPPD: chosenDepartment.ppd,
	};
};
